import React, { useCallback, useMemo, useState } from 'react';
import { Box, List } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { EMPTY_FIELD_VALUE, SHORT_DATE_FORMAT } from '@/common/constants';
import FilePreview from '@/components/FilePreview';

import { LettersOfMedicalNecessity } from '../LettersOfMedicalNecessity.types';
import { LettersOfMedicalNecessityClaims } from '../LettersOfMedicalNecessityClaims/LettersOfMedicalNecessityClaims';
import { LettersOfMedicalNecessityClaimsModal } from '../LettersOfMedicalNecessityClaims/LettersOfMedicalNecessityClaimsModal';

interface Props extends LettersOfMedicalNecessity {
  onView: (id?: string) => void;
  onEdit: (id?: string) => void;
  onDelete: (id?: string) => void;
  isHighlighted?: boolean;
}

const LettersOfMedicalNecessityListItem: React.FC<Props> = ({
  id,
  name,
  description,
  claims,
  validFrom,
  validTo,
  fileUrl,
  onView,
  onEdit,
  onDelete,
  isHighlighted,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);

  const dates = useMemo(() => {
    if (!validFrom && !validTo) {
      return EMPTY_FIELD_VALUE;
    }
    const formattedValidFrom = validFrom ? dayjs(validFrom).format(SHORT_DATE_FORMAT) : EMPTY_FIELD_VALUE;
    const formattedValidTo = validTo ? dayjs(validTo).format(SHORT_DATE_FORMAT) : EMPTY_FIELD_VALUE;
    return `${formattedValidFrom} - ${formattedValidTo}`;
  }, [validFrom, validTo]);

  const handleView = useCallback(() => {
    onView(id);
  }, [id, onView]);

  const handleEdit = useCallback(() => {
    onEdit(id);
  }, [id, onEdit]);

  const handleDelete = useCallback(() => {
    onDelete(id);
  }, [id, onDelete]);

  return (
    <List.Item
      pad={{ vertical: 'spacing12', horizontal: 'spacing24' }}
      testId="lmn-list_item"
      isHighlighted={isHighlighted}
    >
      <Box style={{ flex: 0.6 }} justify="center" onClick={handleView}>
        <FilePreview 
          testId={`lmn-list_item_${id}_file-preview`}
          fileUrl={fileUrl}
          width="61px"
          height="82px"
        />
      </Box>

      <List.Item.Text
        flex={1}
        truncate
        title={name}
        testId={`lmn-list_item_${id}_name`}
        onClick={handleView}
        weight={800}
      >
        {name}
      </List.Item.Text>

      <List.Item.Text
        onClick={handleView}
        weight={400}
        flex={1.8}
        truncate
        title={description}
        testId={`lmn-list_item_${id}_description`}
      >
        {description}
      </List.Item.Text>

      <List.Item.Text weight={400} flex={1} testId={`lmn-list_item_${id}_claims`}>
        <LettersOfMedicalNecessityClaims claims={claims} onShowClaims={() => setIsModalVisible(true)} />
      </List.Item.Text>

      <List.Item.Text weight={400} flex={1} testId={`lmn-list_item_${id}_dates`}>
        {dates}
      </List.Item.Text>

      <Box style={{ flex: 0.5 }} align="end">
        <List.Item.Options>
          <List.Item.Options.Item testId={`lmn-list_item_${id}_view-action`} onClick={handleView}>
            View
          </List.Item.Options.Item>

          <List.Item.Options.Item testId={`lmn-list_item_${id}_edit-action`} onClick={handleEdit}>
            Edit
          </List.Item.Options.Item>

          <List.Item.Options.Item
            testId={`lmn-list_item_${id}_delete-action`}
            color="danger"
            onClick={handleDelete}
          >
            Delete
          </List.Item.Options.Item>
        </List.Item.Options>
      </Box>
      <LettersOfMedicalNecessityClaimsModal
        visible={isModalVisible}
        onSetVisible={setIsModalVisible}
        claims={claims || []}
        lmnName={name  || 'LMN'}
      />
    </List.Item>
  );
};

export default LettersOfMedicalNecessityListItem;
